import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { X, Award, AlertCircle, CheckCircle } from 'lucide-react';
import { planApi, clientApi } from '../services/api';

interface PlanAssignmentModalProps {
  clientId: string;
  onClose: () => void;
}

export function PlanAssignmentModal({ clientId, onClose }: PlanAssignmentModalProps) {
  const queryClient = useQueryClient();
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Fetch client details
  const { data: client } = useQuery({
    queryKey: ['client', clientId],
    queryFn: async () => {
      const response = await clientApi.get(clientId);
      return response.data;
    },
  });

  // Fetch available plans
  const { data: plans = [], isLoading: plansLoading } = useQuery({
    queryKey: ['plans'],
    queryFn: async () => {
      const response = await planApi.list();
      return response.data.filter((plan) => plan.is_published);
    },
  });

  // Assign plan mutation
  const assignMutation = useMutation({
    mutationFn: (planId: string) => planApi.assign(planId, clientId),
    onSuccess: () => {
      setSuccess(true);
      setError(null);
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      queryClient.invalidateQueries({ queryKey: ['client', clientId] });
      setTimeout(() => {
        onClose();
      }, 1500);
    },
    onError: (err: any) => {
      setError(err.message || 'Failed to assign plan. Please try again.');
      setSuccess(false);
    },
  });

  const handleAssign = () => {
    if (!selectedPlanId) {
      setError('Please select a plan');
      return;
    }

    const selectedPlan = plans.find((p) => p.id === selectedPlanId);
    if (!selectedPlan) return;

    // Validate subscription tier
    if (!client?.subscription) {
      setError('Client does not have an active subscription');
      return;
    }

    const tierHierarchy = { free: 0, standard: 1, premium: 2 };
    const clientTier = tierHierarchy[client.subscription.tier];
    const planTier = tierHierarchy[selectedPlan.tier];

    if (clientTier < planTier) {
      setError(
        `Client's ${client.subscription.tier} subscription cannot access ${selectedPlan.tier} tier plans. Please upgrade client subscription.`
      );
      return;
    }

    if (client.subscription.status !== 'active') {
      setError('Client subscription is not active');
      return;
    }

    setError(null);
    assignMutation.mutate(selectedPlanId);
  };

  const tierColors = {
    free: 'bg-gray-100 text-gray-800',
    standard: 'bg-blue-100 text-blue-800',
    premium: 'bg-purple-100 text-purple-800',
  };

  const difficultyColors = {
    beginner: 'bg-green-100 text-green-800',
    intermediate: 'bg-yellow-100 text-yellow-800',
    advanced: 'bg-red-100 text-red-800',
  };

  const clientTier = client?.subscription?.tier || 'free';
  const tierHierarchy = { free: 0, standard: 1, premium: 2 };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Assign Training Plan</h2>
            {client && (
              <p className="text-sm text-gray-600 mt-1">
                {client.first_name} {client.last_name} •{' '}
                <span className="capitalize">{clientTier}</span> tier
              </p>
            )}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {/* Success Message */}
          {success && (
            <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
              <p className="text-sm text-green-800">Plan assigned successfully!</p>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-red-800">Cannot assign plan</p>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          )}

          {/* Subscription Warning */}
          {client && (!client.subscription || client.subscription.status !== 'active') && (
            <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-yellow-800">No Active Subscription</p>
                <p className="text-sm text-yellow-700 mt-1">
                  This client needs an active subscription to be assigned plans.
                </p>
              </div>
            </div>
          )}

          {/* Plans List */}
          {plansLoading ? (
            <div className="text-center py-8 text-gray-500">Loading plans...</div>
          ) : plans.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No published plans available. Create and publish a plan first.
            </div>
          ) : (
            <div className="space-y-3">
              {plans.map((plan) => {
                const planTierLevel = tierHierarchy[plan.tier];
                const clientTierLevel = tierHierarchy[clientTier];
                const canAccess = clientTierLevel >= planTierLevel;
                const isAlreadyAssigned = client?.assigned_plans?.some((p) => p.id === plan.id);

                return (
                  <div
                    key={plan.id}
                    onClick={() => {
                      if (canAccess && !isAlreadyAssigned) {
                        setSelectedPlanId(plan.id);
                        setError(null);
                      }
                    }}
                    className={`border-2 rounded-lg p-4 transition-all cursor-pointer ${
                      selectedPlanId === plan.id
                        ? 'border-indigo-600 bg-indigo-50'
                        : canAccess && !isAlreadyAssigned
                        ? 'border-gray-200 hover:border-indigo-400 hover:shadow-md'
                        : 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900">{plan.title}</h3>
                        {plan.description && (
                          <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                            {plan.description}
                          </p>
                        )}

                        <div className="flex flex-wrap gap-2 mt-3">
                          <span className={`text-xs px-2 py-1 rounded-full ${tierColors[plan.tier]}`}>
                            <Award className="w-3 h-3 inline mr-1" />
                            {plan.tier}
                          </span>
                          <span
                            className={`text-xs px-2 py-1 rounded-full ${
                              difficultyColors[plan.difficulty]
                            }`}
                          >
                            {plan.difficulty}
                          </span>
                          {plan.duration_weeks && (
                            <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-800">
                              {plan.duration_weeks} weeks
                            </span>
                          )}
                        </div>

                        {!canAccess && (
                          <p className="text-xs text-red-600 mt-2">
                            Requires {plan.tier} tier subscription
                          </p>
                        )}
                        {isAlreadyAssigned && (
                          <p className="text-xs text-blue-600 mt-2">Already assigned</p>
                        )}
                      </div>

                      {selectedPlanId === plan.id && (
                        <div className="ml-3">
                          <div className="w-5 h-5 rounded-full bg-indigo-600 flex items-center justify-center">
                            <CheckCircle className="w-4 h-4 text-white" />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleAssign}
            disabled={!selectedPlanId || assignMutation.isPending || success}
            className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {assignMutation.isPending ? 'Assigning...' : 'Assign Plan'}
          </button>
        </div>
      </div>
    </div>
  );
}
